<template>
  <v-container
    id="login"
    class="fill-height justify-center"
    tag="section"
  >
    <v-row justify="center">
      <v-col
        md="6"
        sm="9"
      >
        <v-slide-y-transition appear>
          <base-material-card
            color="success"
            light
            max-width="100%"
            min-width="400"
            class="px-5 py-3"
          >
            <template v-slot:heading>
              <div class="text-center">
                <h1 class="display-2 font-weight-bold mb-2">
                  Upload PDFs for Branding
                </h1>
              </div>
            </template>

            <v-card-text class="text-center">
              <div>
                <v-form
                  v-model="valid"
                  refs="form"
                >
                  <h1 class="pt-6">
                    Step 1: Select Header
                  </h1>
                  <v-row justify="center">
                    <v-radio-group
                      v-model="header"
                      row
                      mandatory
                      class="ml-0 pt-6"
                    >
                      <v-radio
                        label="Safety Data Sheet"
                        color="success"
                        value="sds"
                      />

                      <v-radio
                        label="Product Data Sheet"
                        color="success"
                        value="pds"
                      />
                      <v-radio
                        label="Standard Header"
                        color="success"
                        value="standard"
                      />
                    </v-radio-group>
                  </v-row>
                  <h1 class="pt-6 pb-6">
                    Step 2: Select PDFs
                  </h1>
                  <v-file-input
                    v-model="files"
                    class="pt-6"
                    color="success"
                    counter
                    label="PDFs Input"
                    placeholder="Select your PDFs"
                    prepend-icon="mdi-paperclip"
                    outlined
                    accept=".pdf"
                    show-size
                    multiple
                    :rules="fileRules"
                    error-count="5"
                    :disabled="fileDownloads.length > 0"
                    :display-size="1000"
                  >
                    <template v-slot:selection="{ index, text }">
                      <v-chip
                        v-if="index < 2"
                        color="success"
                        dark
                        label
                        small
                      >
                        {{ text }}
                      </v-chip>

                      <span
                        v-else-if="index === 2"
                        class="overline grey--text text--darken-3 mx-2"
                      >
                        +{{ files.length - 2 }} File(s)
                      </span>
                    </template>
                  </v-file-input>
                  <v-btn
                    large
                    color=""
                    depressed
                    :disabled="!valid || loading || fileDownloads.length > 0"
                    :loading="loading"
                    class="v-btn--text success--text"
                    @click="submitHandler"
                  >
                    Submit
                  </v-btn>
                  <v-alert
                    v-model="showError"
                    dismissible
                    icon="mdi-bell"
                    color="error"
                  >
                    ERROR - An error occured processing your PDFs, please try again later. If this continues
                    to occur please contact support@mcalpinefree.co.nz
                  </v-alert>
                </v-form>
                <div v-show="fileDownloads.length > 0">
                  <h1 class="pt-6 pb-6">
                    Step 3: Download processed PDFs
                  </h1>
                  <v-simple-table>
                    <thead>
                      <tr>
                        <th class="success--text">
                          #
                        </th>
                        <th class="success--text">
                          File name
                        </th>
                        <th class="text-right success--text">
                          Download
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr
                        v-for="file in fileDownloads"
                        :key="file.number"
                        @click="downloadClicked(file.link, file.name)"
                      >
                        <td class="text-left">
                          {{ file.number }}
                        </td>
                        <td class="text-left">
                          {{ file.name }}
                        </td>
                        <td class="text-right">
                          <v-btn
                            color="success"
                            class="px-1 ml-1"
                            fab
                            small
                          >
                            <v-icon>mdi-file-download-outline</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <v-btn
                    large
                    color=""
                    depressed
                    class="v-btn--text success--text"
                    @click="startOverHandler"
                  >
                    Start again: Upload more files
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </base-material-card>
        </v-slide-y-transition>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import axios from 'axios'

  // Function to download data to a file
  function download (data, filename, type) {
    var file = new Blob([data], { type: type })
    if (window.navigator.msSaveOrOpenBlob) { // IE10+
      window.navigator.msSaveOrOpenBlob(file, filename)
    } else { // Others
      var a = document.createElement('a')
      var url = URL.createObjectURL(file)
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      setTimeout(function () {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }, 0)
    }
  }

  async function getUploadLocations (fileNames, headerType) {
    var result = await axios.post(`${process.env.VUE_APP_API_URL}/add_header_job`, {
      template: headerType,
      files: fileNames,
      front_page_only: false,
    })
    return result.data
  }

  function readPDFAsync (file) {
    return new Promise((resolve, reject) => {
      var reader = new FileReader()
      reader.onload = (e) => {
        var data = e.target.result
        resolve(data)
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(file)
    })
  }

  export default {
    name: 'Uploader',

    data: () => ({
      files: [],
      valid: false,
      loading: false,
      showError: false,
      header: '',
      fileRules: [
        value => value.length !== 0 || 'A file is required',
        value => value.every(v => v.name.toLowerCase().endsWith('.pdf')) || 'All files need to be PDF files',
        value => value.reduce((acc, cur) => acc + cur.size, 0) < 20000000 || 'Total file size is too large (over 20MB), try uploading files separately',
      ],
      fileDownloads: [],
    }),

    methods: {
      startOverHandler: function () {
        this.files = []
        this.loading = false
        this.fileDownloads = []
      },
      downloadClicked: async function (link, fileName) {
        try {
          var result = await axios.get(link, {
            responseType: 'blob',
          })
          download(result.data, fileName, 'application/pdf')
        } catch (err) {
          this.showError = true
        }
      },
      submitHandler: async function (event) {
        if (this.valid) {
          // analytics
          this.loading = true
          try {
            window.plausible('SubmitPDF')
          } catch (err) { /** Ignore analytics error */ }

          var fileNames = this.files.map(f => f.name)
          try {
            var uploadInfo = await getUploadLocations(fileNames, this.header)
          } catch (err) {
            this.loading = false
            this.showError = true
          }
          var jobId = uploadInfo.id
          var uploadUrlsDict = uploadInfo.upload_urls
          var uploadOptions = {
            headers: {
              'Content-Type': 'application/pdf',
            },
          }

          if (this.files.length !== Object.keys(uploadUrlsDict).length) {
            this.loading = false
            this.showError = true
          }

          for (let i = 0; i < this.files.length; i++) {
            var fileData = await readPDFAsync(this.files[i])
            var uploadLocation = uploadUrlsDict[this.files[i].name]
            await axios.put(uploadLocation, fileData, uploadOptions)
          }

          try {
            var result = await axios.get(`${process.env.VUE_APP_API_URL}/add_header_job/${jobId}`)

            var resultDict = result.data.file_urls

            var keys = Object.keys(resultDict)
            this.fileDownloads = []
            var count = 1
            keys.forEach(key => {
              var fileInfo = {
                name: key,
                number: count,
                link: resultDict[key],
              }
              this.fileDownloads.push(fileInfo)
              count++
            })
          } catch (err) {
            this.loading = false
            this.showError = true
          }

          this.loading = false
        }
      },
    },
  }
</script>
